import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, CheckCircle, ArrowRight, AlertCircle, Target, CheckCircle2, Rocket } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ProgressRing } from "@/components/ui/progress-ring";
import { cn } from "@/lib/utils";

interface StepAnalysisPreviewProps {
  workspaceId: string;
  role: string;
  title: string | null;
  decisionType: string;
  decisionSubtype: string;
  trigger: string;
  stakes: string;
  previousAttempts: string;
  impactScore: number;
  urgencyScore: number;
  onEdit?: () => void;
}

interface AnalysisResult {
  sessionId: string;
  portalSummary: string;
  initialTension: string;
  firstQuestion: string;
  paraphrase: string;
  confidenceLevel: number;
}

type AnalysisPhase = "context" | "analyzing" | "question" | "complete";

export const StepAnalysisPreview = ({
  workspaceId,
  role,
  title,
  decisionType,
  decisionSubtype,
  trigger,
  stakes,
  previousAttempts,
  impactScore,
  urgencyScore,
  onEdit,
}: StepAnalysisPreviewProps) => {
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [displayedSummary, setDisplayedSummary] = useState("");
  const [displayedTension, setDisplayedTension] = useState("");
  const [displayedQuestion, setDisplayedQuestion] = useState("");
  const [showContent, setShowContent] = useState(false);
  const [analysisPhase, setAnalysisPhase] = useState<AnalysisPhase>("context");
  const [progress, setProgress] = useState(0);
  const [isConfirming, setIsConfirming] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  
  const navigate = useNavigate();
  const { toast } = useToast();

  // Lock body scroll when transition is active
  useEffect(() => {
    if (showTransition) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showTransition]);

  // Typing effect hook
  const useTypingEffect = (text: string, speed: number = 30) => {
    const [displayed, setDisplayed] = useState("");
    const [isComplete, setIsComplete] = useState(false);

    useEffect(() => {
      if (!text) return;
      
      let index = 0;
      setDisplayed("");
      setIsComplete(false);

      const timer = setInterval(() => {
        if (index < text.length) {
          setDisplayed(text.slice(0, index + 1));
          index++;
        } else {
          setIsComplete(true);
          clearInterval(timer);
        }
      }, speed);

      return () => clearInterval(timer);
    }, [text, speed]);

    return { displayed, isComplete };
  };

  const summaryTyping = useTypingEffect(displayedSummary, 20);
  const tensionTyping = useTypingEffect(displayedTension, 25);
  const questionTyping = useTypingEffect(displayedQuestion, 30);

  useEffect(() => {
    const performAnalysis = async () => {
      try {
        const {
          data: { user },
          error: authError,
        } = await supabase.auth.getUser();

        if (authError || !user) {
          console.error("Auth error:", authError);
          toast({
            title: "Fehler",
            description: "Du musst angemeldet sein.",
            variant: "destructive",
          });
          setIsAnalyzing(false);
          navigate("/auth");
          return;
        }

        setAnalysisPhase("context");
        setProgress(0);
        await new Promise(resolve => setTimeout(resolve, 400));
        setProgress(25);

        setAnalysisPhase("analyzing");
        await new Promise(resolve => setTimeout(resolve, 400));
        setProgress(50);

        const response = await supabase.functions.invoke("phase1-start", {
          body: {
            workspace_id: workspaceId,
            user_id: user.id,
            role: role,
            title: title || null,
            decision_type: decisionType,
            decision_subtype: decisionSubtype,
            trigger: trigger,
            stakes: stakes,
            previous_attempts: previousAttempts,
            impact_score: impactScore,
            urgency_score: urgencyScore,
          },
        });

        setProgress(75);

        if (response.error) {
          console.error("Error starting session:", response.error);
          toast({
            title: "Fehler",
            description: "Session konnte nicht gestartet werden. Bitte versuche es erneut.",
            variant: "destructive",
          });
          setIsAnalyzing(false);
          return;
        }

        const data = response.data;

        if (!data.session_id) {
          toast({
            title: "Fehler",
            description: "Keine Session-ID erhalten.",
            variant: "destructive",
          });
          setIsAnalyzing(false);
          return;
        }

        setAnalysisPhase("question");
        await new Promise(resolve => setTimeout(resolve, 400));
        setProgress(100);

        // Extract from nested analysis object (phase1-start returns analysis as nested object)
        const analysisData = data.analysis || {};
        const analysisResult: AnalysisResult = {
          sessionId: data.session_id,
          portalSummary: analysisData.portal_summary || `Du bist ${role} und stehst vor einer Entscheidung im Bereich ${decisionType}.`,
          initialTension: analysisData.initial_tension || "Diese Entscheidung erfordert sorgfältige Abwägung verschiedener Faktoren.",
          firstQuestion: data.first_question_text || "Was ist der konkrete Entscheidungskern, den du wirklich klären willst?",
          paraphrase: analysisData.paraphrase || '',
          confidenceLevel: analysisData.confidence_level || 70,
        };

        await new Promise(resolve => setTimeout(resolve, 500));
        setAnalysisPhase("complete");

        setAnalysis(analysisResult);
        setIsAnalyzing(false);
        
        setTimeout(() => setShowContent(true), 300);
        setTimeout(() => setDisplayedSummary(analysisResult.portalSummary), 500);
        setTimeout(() => setDisplayedTension(analysisResult.initialTension), 
          500 + analysisResult.portalSummary.length * 20 + 800);
        setTimeout(() => setDisplayedQuestion(analysisResult.firstQuestion), 
          500 + analysisResult.portalSummary.length * 20 + 800 + analysisResult.initialTension.length * 25 + 800);

      } catch (error) {
        console.error("Error in performAnalysis:", error);
        toast({
          title: "Fehler",
          description: "Ein unerwarteter Fehler ist aufgetreten.",
          variant: "destructive",
        });
        setIsAnalyzing(false);
      }
    };

    performAnalysis();
  }, []);

  const handleConfirm = () => {
    if (analysis?.sessionId && !isConfirming) {
      setIsConfirming(true);
      
      // Show transition overlay after button animation
      setTimeout(() => {
        setShowTransition(true);
      }, 400);
      
      // Navigate after transition is visible
      setTimeout(() => {
        toast({
          title: "Session gestartet",
          description: "Deine Entscheidungs-Session wurde erfolgreich erstellt.",
        });
        navigate(`/app/session/${analysis.sessionId}`, {
          state: {
            firstQuestion: analysis.firstQuestion,
            paraphrase: analysis.paraphrase,
            questionId: 'q_problem_initial'
          }
        });
      }, 1200);
    }
  };

  const handleCorrect = () => {
    toast({
      title: "Zurück zur Bearbeitung",
      description: "Du kannst deine Angaben anpassen.",
    });
    onEdit?.();
  };

  if (isAnalyzing) {
    const phaseLabels: Record<AnalysisPhase, string> = {
      context: "Kontext verstehen...",
      analyzing: "Situation analysieren...",
      question: "Frage personalisieren...",
      complete: "Analyse abgeschlossen"
    };

    return (
      <div className="flex flex-col items-center justify-center min-h-[450px] space-y-8 animate-fade-in">
        {/* Premium loading ring */}
        <div className="relative">
          <ProgressRing progress={progress} size={140} strokeWidth={8}>
            <div className="flex flex-col items-center gap-1">
              <Sparkles className="h-10 w-10 text-primary animate-pulse" />
              <span className="text-xl font-bold text-primary">
                {progress}%
              </span>
            </div>
          </ProgressRing>
          {/* Glow effect */}
          <div className="absolute inset-0 bg-primary/10 rounded-full blur-2xl -z-10" />
        </div>
        
        <div className="text-center space-y-3 max-w-md">
          <h2 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
            Quentari analysiert deine Situation
          </h2>
          <p className="text-base text-primary font-medium animate-pulse">
            {phaseLabels[analysisPhase]}
          </p>
          <p className="text-sm text-muted-foreground">
            Ich verstehe den Kontext und bereite die perfekte Startfrage vor.
          </p>
        </div>

        {/* Phase indicators */}
        <div className="flex gap-6 text-sm">
          {[
            { label: "Kontext", threshold: 25 },
            { label: "Analyse", threshold: 75 },
            { label: "Frage", threshold: 100 },
          ].map((phase, index) => (
            <div 
              key={index}
              className={cn(
                "flex items-center gap-2 transition-all duration-500",
                progress >= phase.threshold ? "text-primary" : "text-muted-foreground"
              )}
            >
              <div className={cn(
                "h-2 w-2 rounded-full transition-all duration-500",
                progress >= phase.threshold ? "bg-primary scale-125" : "bg-border"
              )} />
              <span>{phase.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!analysis || !showContent) {
    return null;
  }

  const getConfidenceLabel = (level: number) => {
    if (level < 40) return { label: "Niedrig", color: "text-amber-500", bg: "bg-amber-500/10" };
    if (level < 60) return { label: "Mittel", color: "text-blue-500", bg: "bg-blue-500/10" };
    if (level < 80) return { label: "Hoch", color: "text-green-500", bg: "bg-green-500/10" };
    return { label: "Sehr hoch", color: "text-emerald-600", bg: "bg-emerald-500/10" };
  };

  const confidenceLabel = analysis ? getConfidenceLabel(analysis.confidenceLevel) : null;

  const getTensionBullets = (tension: string): string[] => {
    const bullets = tension
      .split(/[.!?]\s+|\n/)
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .slice(0, 3);
    return bullets.length > 0 ? bullets : [tension];
  };

  const tensionBullets = displayedTension ? getTensionBullets(displayedTension) : [];
  const bulletIcons = [AlertCircle, Target, CheckCircle2];


  return (
    <>
      {/* Enterprise-Level Fullscreen Transition Overlay */}
      {showTransition && (
        <div className="fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[100] overflow-hidden">
          {/* Animated Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/5" />
          <div className="absolute inset-0 backdrop-blur-xl bg-background/95" />
          
          {/* Floating Particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <div 
                key={i}
                className="absolute w-2 h-2 rounded-full bg-primary/30 animate-glow-pulse"
                style={{ 
                  left: `${10 + i * 12}%`, 
                  top: `${15 + (i % 3) * 25}%`,
                  animationDelay: `${i * 0.2}s`
                }}
              />
            ))}
          </div>
          
          {/* Sweeping Lines */}
          <div 
            className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent animate-line-sweep"
            style={{ animationDelay: '0.3s' }}
          />
          <div 
            className="absolute top-2/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent animate-line-sweep"
            style={{ animationDelay: '0.8s' }}
          />
          
          {/* Center Content */}
          <div className="relative z-10 h-full flex flex-col items-center justify-center">
            {/* Icon with Glow */}
            <div className="relative animate-scale-in">
              {/* Outer Glow Ring */}
              <div className="absolute -inset-12 rounded-full bg-primary/10 blur-3xl animate-glow-pulse" />
              <div className="absolute -inset-8 rounded-full bg-primary/15 blur-2xl animate-glow-pulse" style={{ animationDelay: '0.5s' }} />
              
              {/* Progress Ring SVG */}
              <svg 
                className="absolute -inset-6 w-[calc(100%+3rem)] h-[calc(100%+3rem)] animate-spin-slow"
                viewBox="0 0 100 100"
              >
                <circle 
                  cx="50" 
                  cy="50" 
                  r="46" 
                  fill="none" 
                  stroke="hsl(var(--primary)/0.15)" 
                  strokeWidth="1.5"
                />
                <circle 
                  cx="50" 
                  cy="50" 
                  r="46" 
                  fill="none" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth="1.5"
                  strokeDasharray="80 200"
                  strokeLinecap="round"
                />
              </svg>
              
              {/* Icon Container */}
              <div className="flex h-28 w-28 items-center justify-center rounded-3xl bg-card border border-primary/20 shadow-2xl">
                <Rocket className="h-12 w-12 text-primary" />
              </div>
            </div>
            
            {/* Text Content - Staggered Animation */}
            <div className="mt-12 text-center space-y-4">
              <span 
                className="block text-xs font-bold uppercase tracking-[0.3em] text-primary opacity-0 animate-fade-in-up"
                style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}
              >
                Phase 1
              </span>
              <h2 
                className="text-2xl md:text-3xl font-bold font-serif text-foreground opacity-0 animate-fade-in-up"
                style={{ animationDelay: '450ms', animationFillMode: 'forwards' }}
              >
                Entscheidung schärfen
              </h2>
              <p 
                className="text-sm text-muted-foreground opacity-0 animate-fade-in-up"
                style={{ animationDelay: '600ms', animationFillMode: 'forwards' }}
              >
                Dein Entscheidungsrahmen wird geladen...
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className={cn(
        "space-y-8 animate-fade-in transition-all duration-500",
        showTransition && "opacity-0 scale-95 blur-sm pointer-events-none"
      )}>
      <div className="space-y-3">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-green-500/10 text-green-600">
            <CheckCircle className="h-5 w-5" />
          </div>
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Analyse vorbereitet</span>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">
          Der Entscheidungsrahmen
        </h1>
        <p className="text-sm text-muted-foreground">
          Prüfe kurz, ob die Zusammenfassung deine Situation korrekt erfasst.
        </p>
      </div>

      <div className="space-y-4">
        {/* Portal Summary */}
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 space-y-3 backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="space-y-2 flex-1 min-w-0">
              <h3 className="font-semibold text-base text-foreground">
                Kurz zusammengefasst
              </h3>
              <p className="text-sm leading-relaxed text-foreground/90">
                {summaryTyping.displayed}
                {!summaryTyping.isComplete && (
                  <span className="inline-block w-1.5 h-4 bg-primary ml-1 animate-pulse" />
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Initial Tension */}
        {displayedTension && (
          <div className="rounded-xl border border-border/60 bg-card/50 backdrop-blur-sm p-5 space-y-3 animate-fade-in">
            <h3 className="font-semibold text-base text-foreground">
              Worum es im Kern geht
            </h3>
            <ul className="space-y-2">
              {tensionBullets.map((bullet, index) => {
                const Icon = bulletIcons[index] || AlertCircle;
                return (
                  <li key={index} className="flex items-start gap-3 text-sm leading-relaxed text-muted-foreground">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-muted/50 text-muted-foreground mt-0.5">
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <span>
                      {bullet}
                      {index === tensionBullets.length - 1 && !tensionTyping.isComplete && (
                        <span className="inline-block w-1.5 h-4 bg-primary ml-1 animate-pulse" />
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* First Question - Phase 1 Entry */}
        {displayedQuestion && (
          <div className="space-y-4 animate-fade-in">
            {/* Phase Header */}
            <div className="flex items-center gap-3 pt-2">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
              <span className="text-xs font-semibold text-primary uppercase tracking-widest px-3">
                Phase 1 – Entscheidung schärfen
              </span>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
            </div>
            
            <div className="rounded-xl border-2 border-primary bg-card/50 backdrop-blur-sm p-5 space-y-4 shadow-[0_0_30px_hsl(var(--primary)/0.1)]">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Rocket className="h-4 w-4 text-primary" />
                  <h3 className="font-semibold text-sm text-foreground">
                    Empfohlene Einstiegsfrage:
                  </h3>
                </div>
                <p className="text-lg md:text-xl font-semibold leading-relaxed text-primary">
                  {questionTyping.displayed}
                  {!questionTyping.isComplete && (
                    <span className="inline-block w-2 h-5 bg-primary ml-1 animate-pulse" />
                  )}
                </p>
              </div>
              
              {confidenceLabel && (
                <div className="pt-3 border-t border-border/50">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Treffsicherheit</span>
                    <span className={cn(
                      "font-semibold px-2 py-0.5 rounded-full text-xs",
                      confidenceLabel.color, confidenceLabel.bg
                    )}>
                      {confidenceLabel.label}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {questionTyping.isComplete && (
        <div className={cn(
          "flex flex-col sm:flex-row gap-3 pt-2 animate-fade-in transition-opacity duration-300",
          isConfirming && "opacity-50 pointer-events-none"
        )}>
          <Button
            onClick={handleConfirm}
            disabled={isConfirming}
            className={cn(
              "h-12 px-8 text-base font-semibold flex-1 sm:flex-initial gap-2 rounded-xl transition-all duration-300",
              isConfirming && "scale-95"
            )}
          >
            {isConfirming ? (
              <>
                <CheckCircle className="h-5 w-5 animate-scale-in" />
                Wird gestartet...
              </>
            ) : (
              <>
                Das passt so – Weiter
                <ArrowRight className="h-5 w-5" />
              </>
            )}
          </Button>
          <Button
            onClick={handleCorrect}
            variant="outline"
            disabled={isConfirming}
            className="h-12 px-6 text-base font-semibold rounded-xl border-border/60 hover:bg-card hover:border-primary/40"
          >
            Ich möchte etwas anpassen
          </Button>
        </div>
      )}
      </div>
    </>
  );
};
